import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIsMounted } from 'usehooks-ts';
import { useLocaleContext } from '../../../../../context/LocaleContext';
import { useModalContext } from '../../../../../context/ModalContext';
import { useScheduleContext } from '../../../../../context/ScheduleContext';
import { unixDiff } from '../../../../../helpers/times';
import { removeDuplicates } from '../../../../../helpers/remove-duplicates';
import { findEpisodesTimeCrossing } from '../../../../../helpers/schedule-helper';
import ScheduleGridCard from '../../../ScheduleCards/ScheduleCardOffline/ScheduleGridCard';
import ScheduleDetailsModal from '../../../ScheduleDetailsModal';
import ScheduleBreakCard from '../../../ScheduleCards/ScheduleCardOffline/ScheduleBreakCard';
import MemberDetailsModal from '../../../../Members/MembersComponents/MemberDetailsModal';
import CButton from '../../../../UI/CButton';

import './style.scss';

const COLUMN_GAP = 5;

const ScheduleGridStreams = ({
    streams,
    episodes,
    timelineData,
    minuteStep,
    colMinWidth = 280,
    episodesOffset = 0,
    page,
    widgetSettings = null,
    card = null,
    isStreamsHidden = false,
    currentUser,
    checkAuth,
    onRateEpisode,
}) => {
    const $locale = useLocaleContext();
    const modalContext = useModalContext();
    const {isWidget} = useScheduleContext();
    const isMounted = useIsMounted();
    const container = useRef(null);
    const [isStart, setIsStart] = useState(true);
    const [isEnd, setIsEnd] = useState(false);

    useEffect(() => {
        if (!container.current) return;

        setIsStart(container.current.scrollLeft === 0);
        setIsEnd(!!container.current && container.current.scrollLeft + container.current.offsetWidth === container.current.scrollWidth);
    }, [episodes]);

    /** Метод для рассчета ширины и смещения важных эпизодов с подробными комментариями чтобы не забыть */
    const getEpisodeWidth = useCallback((episode) => {
        if (!isMounted || !container.current) {
            return 'auto';
        }

        /** Нода текущего эпизода. Нужна для указания стилей для смещения и получения оригинальной ширины */
        const episodeCardNode = container.current?.querySelector(`[data-episode="${episode.id}"]`) ?? null;
        
        /** Если нода не определена, выставляем width в авто (значение по умолчанию) */
        if (!episodeCardNode) {
            return 'auto';
        }
        /** Устанавливаем data атрибут с оригинальной шириной карточки  */
        if (!episodeCardNode.dataset.originalWidth) {
            episodeCardNode.dataset.originalWidth = episodeCardNode.offsetWidth;
        }
        
        /** Оригинальная ширина карточки эпизода. Если карточка была модифицирована, тогда принимается значение из data аттрибута.
         * В ином случае берется текущее значение ширины карточки.
         */
        const originalCardWidth = episodeCardNode.dataset.originalWidth || episodeCardNode.offsetWidth;

        /** РАССЧЕТ ДЛИНЫ ВАЖНЫХ ЭПИЗОДОВ */
        /** Index колонки текущего эпизода */
        const currentEpisodeColumnIndex = streams.findIndex((stream) => stream.episode.some(({id}) => id === episode.id));
        /** Массив id пересекающихся эпизодов */
        const crossingEpisodes = removeDuplicates(findEpisodesTimeCrossing(episode, episodes), 'streamId').map(({id}) => id);
        /** Массив id стримов/колонок */
        const streamsIds = streams.map(({id}) => id);
        /** Массив id стримов/колонок в которых есть пересекающиеся эпизоды */
        const crossingStreamsIds = streams.filter(({episode}) => {
            return episode.some(({id}) => crossingEpisodes.includes(id));
        }).map(({id}) => id);
        /** Массив булевых значения в котором указаны как true колонки без пересечений, false - с пересечением.
         * Это нужно чтобы отсеить лишние колонки, которые не должны использоваться в рассчетах ширины и смещении.
         * Можно конечно и на основе массива id, но так будет проще КМК.
         */
        const streamsMatrix = !crossingStreamsIds.length
            ? Array(streamsIds.length).fill(true)
            : streamsIds
                .reduce((acc, id, index) => {
                    const isCrossing = crossingStreamsIds.includes(id);

                    if (index > 0 && streamsIds.indexOf(episode.streamId) < index && !acc[index - 1]) {
                        acc.push(false);
                    }

                    if (!isCrossing) {
                        acc.push(true);
                    } else {
                        acc.push(false);
                    }

                    return acc;
                }, []);

        /** Рассчет кол-ва колонок для заполнения эпизодом.
         * Перый итератор считает кол-во пустых колонок со сдвигом вправо от текущей, до первого пересечения.
         * Второй итератор делает то же самое, но со сдвигом влево (к началу).
         */
        const slicedRightMatrix = streamsMatrix.slice(currentEpisodeColumnIndex + 1);
        let rightMultiplier = 0;
        for (let i = 0; i < slicedRightMatrix.length; i++) {
            if (!slicedRightMatrix[i]) {
                break;
            }

            rightMultiplier += 1;
        }

        const slicedLeftMatrix = streamsMatrix.slice(0, currentEpisodeColumnIndex).reverse();
        let leftMultiplier = 0;
        for (let i = 0; i < slicedLeftMatrix.length; i++) {
            if (!slicedLeftMatrix[i]) {
                break;
            }

            leftMultiplier += 1;
        }

        /** Кол-во колонок для заполнения. 1 - это текущая колонка эпизода */
        const episodeCardWidthMultiplier = rightMultiplier + leftMultiplier + 1;

        /** Множитель для отступов учавствующих в рассчете ширины.
         * Тк у колонки по 2 отстпупа, то умножаем кол-во колонок для заполнения на 2 и вычитам 2 крайних отступа.
         */
        const gapFactor = episodeCardWidthMultiplier * 2 - 2;
        /** Общая ширина отступов для учета в ширине кароточки.
         * Если есть пересекающиеся эпизоды, то умножаем ширину отступа на множитель отступов.
         */
        const gapOffset = episodeCardWidthMultiplier > 0 ? COLUMN_GAP * gapFactor : 0;
        /** Рассчет общей ширины карточки с учетом колонок для заполнения и отступов внутри колонок */
        const episodeCardWidth = originalCardWidth * episodeCardWidthMultiplier + gapOffset;
        
        /** КОНЕЦ РАССЧЕТА ДЛИНЫ */

        /** РАССЧЕТ СМЕЩЕНИЯ ЭПИЗОДА */
        /** Если карточка в первой колонке то смещение ей не нужно */
        if (currentEpisodeColumnIndex !== 0) {
            /** Кол-во пикселей на которые нужно сдвинуть карточку влево.
             * В право сдвигать не нужно, карточка сама увеличит длину на нужное кол-во.
             */
            const episodeWidthOffset = originalCardWidth * leftMultiplier;
            /** Рассчет отступов в колонках для сдвига. Умножаем ширину отступа на 2 (2 шт в колонке), 
             * потом на кол-во пустых колонок и вычитаем один отступ от крайней колонки (чтобы не прилипал) 
             */
            const episodeGapOffset = COLUMN_GAP * 2 * leftMultiplier - COLUMN_GAP;

            /** применяем сдвиг. Если episodeWidthOffset = 0, то сдвиг не требуется. */
            episodeCardNode.style.left = !!episodeWidthOffset
                ? `-${episodeWidthOffset + episodeGapOffset}px`
                : null;
        }
        /** КОНЕЦ РАССЧЕТА СМЕЩЕНИЯ */

        return episodeCardWidth;
    }, [isMounted, episodes]);

    function handleScheduleSlide(direction) {
        if (!container.current) return;

        if (direction === 'right') {
            container.current.scrollLeft = container.current.scrollLeft + container.current.offsetWidth;
        } else {
            container.current.scrollLeft -= container.current.offsetWidth;
        }
        setIsStart(container.current.scrollLeft === 0);
        setIsEnd(!!container.current && container.current.scrollLeft + container.current.offsetWidth === container.current.scrollWidth);
    }

    if (!streams?.length || !timelineData) {
        return null;
    }

    return (
        <div
            ref={container}
            className={`schedule-grid-streams container-scroll container-scroll--horizontal ${isStreamsHidden ? 'streams-hidden' : ''}`}
        >
            <div
                className={`schedule-grid-streams__columns`}
                style={{
                    gridTemplateColumns: `repeat(${streams?.length}, minmax(${colMinWidth}px, 1fr))`
                }}
            >
                {
                    streams.map((stream) => (
                        <div
                            key={stream.id}
                            className="schedule-grid-streams-col"
                        >
                            {
                                !isStreamsHidden &&
                                <div className="schedule-grid-streams-col__name">
                                    <div
                                        className="schedule-grid-streams-col__name-color"
                                        style={{
                                            backgroundColor: stream?.color || null,
                                        }}
                                    />
                                    <span className="schedule-grid-streams-col__name-description">
                                        {$locale.getProperty(stream, 'description')}
                                    </span>
                                </div>
                            }
                            {
                                episodes.filter((episode) => episode.stream.id === stream.id)
                                    .map((episode) => {
                                        if (card) {
                                            return (
                                                React.createElement(
                                                    card,
                                                    {
                                                        key: episode.id,
                                                        episode,
                                                        cardStyles: {
                                                            top: unixDiff(timelineData.timeStart, episode.timeStart) * minuteStep - episodesOffset + 3,
                                                            height: unixDiff(episode.timeStart, episode.timeStop) * minuteStep - 4,
                                                            width: episode.importantEpisode ? getEpisodeWidth(episode) : 'auto',
                                                            borderTop: (!!episode.stream?.color && !episode.delay) ? `2px solid ${episode.stream.color}` : 'none',
                                                        },
                                                    }
                                                )
                                            )
                                        }

                                        return (
                                            !episode.delay
                                                ? <ScheduleGridCard
                                                    key={episode.id}
                                                    episode={episode}
                                                    cardStyles={{
                                                        top: unixDiff(timelineData.timeStart, episode.timeStart) * minuteStep - episodesOffset + 3, // +3 для отступа сверху между карточками
                                                        height: unixDiff(episode.timeStart, episode.timeStop) * minuteStep - 4, // -4 для отступа снизу между карточками
                                                        width: episode.importantEpisode ? getEpisodeWidth(episode) : 'auto',
                                                    }}
                                                    settings={!!widgetSettings ? widgetSettings : page?.settings ?? null}
                                                    showHall={streams.length > 1}
                                                    onEpisodeClick={(episode) => {
                                                        modalContext?.renderModal(
                                                            ScheduleDetailsModal,
                                                            {episode, lockPageScroll: false, settings: !!widgetSettings ? widgetSettings : page?.settings ?? null},
                                                            null,
                                                            (isWidget ? !!widgetSettings?.showEpisodePopup : true)
                                                        )
                                                    }}
                                                    onMemberSelect={(member) => {
                                                        modalContext?.renderModal(
                                                            MemberDetailsModal,
                                                            {member, lockPageScroll: false, settings: !!widgetSettings ? widgetSettings : page?.settings ?? null},
                                                            null,
                                                            (isWidget ? !!widgetSettings?.showMemberPopup : true)
                                                        )
                                                    }}
                                                    checkAuth={checkAuth}
                                                    onRateEpisode={onRateEpisode}
                                                    currentUser={currentUser}
                                                />
                                                : <ScheduleBreakCard
                                                    key={episode.id}
                                                    episode={episode}
                                                    attributes={{
                                                        'data-episode': episode.id,
                                                    }}
                                                    // data-episode={}
                                                    isGridView
                                                    cardStyles={{
                                                        top: unixDiff(timelineData.timeStart, episode.timeStart) * minuteStep - episodesOffset + 3,
                                                        height: unixDiff(episode.timeStart, episode.timeStop) * minuteStep - 4,
                                                        width: episode.importantEpisode ? getEpisodeWidth(episode) : 'auto',
                                                    }}
                                                />
                                        )
                                    })
                            }
                        </div>
                    ))
                }
                {
                    !isStart && 
                    <CButton
                        className="schedule-grid-streams-slide-btn schedule-grid-streams-slide-btn--left background-blur"
                        icon="chevron_left"
                        round
                        style={
                            window.self !== window.top ? {
                                top: container.current?.getBoundingClientRect()?.top ?? 200,
                                left: 24,
                            } : {}
                        }
                        onClick={() => handleScheduleSlide('left')}
                    />
                }
                {
                    !isEnd &&
                    <CButton
                        className="schedule-grid-streams-slide-btn schedule-grid-streams-slide-btn--right background-blur"
                        icon="chevron_right"
                        round
                        style={
                            window.self !== window.top ? {
                                top: container.current?.getBoundingClientRect()?.top ?? 200,
                                right: 24,
                            } : {}
                        }
                        onClick={() => handleScheduleSlide('right')}
                    />
                }
            </div>
        </div>
    )
};

export default ScheduleGridStreams;
